import { gql } from '@apollo/client';

export interface CrunchbaseCompany {
    uuid: string;
    name: string;
    permalink?: string;
    description?: string;
    funding?: number;
    employees?: string;
    lastFundingType?: string;
    imageId?: string;
}

export interface CrunchbaseCompanyFilterQueryParams {
    employee_count?: { _in: string[] };
    total_funding_usd?: { _lte?: number; _gte?: number };
    founded_year?: { _lte?: number; _gte?: number };
    last_funding_type?: { _in: string[] };
    last_funding_date?: { _lte?: string; _gte?: string };
    categories?: { category: { _in: string[] } };
    status?: { _in: string[] };
    country_code?: { _in: string[] };
    region?: { _in: string[] };
    city?: { _in: string[] };
    name?: { _iregex: string };
    lower_name?: { _ilike: string };
    company_investors?: { investor_uuid: { _in: string[] } };
}

export interface CBCategories {
    name: string;
}

export interface CBFundingRounds {
    round: string;
}

export interface CBInvestor {
    name: string;
    uuid: string;
    investment_count: number;
}

export interface CBCompanyStatuses {
    status: string;
}

export interface CBCountries {
    countryCode: string;
}

export interface CBRegions {
    region: string;
}

export interface CBCities {
    city: string;
}

export interface CrunchbaseOrganization {
    uuid: string;
    shortDescription: string;
    numEmployees: string;
    totalFundingUSD: number;
    permalink: string;
    foundedYear: number;
    status: string;
    revenueRange: string;
    lastFundingType: string;
    lastFundingDate: string;
    valuationUSD: number;
    valuationDate: string;
    city: string;
    region: string;
    countryCode: string;
    company_investors: Array<{
        investorName: string;
        investor: {
            permalink: string;
        };
    }>;
    recruiter_url: {
        url: string;
    };
}

export const CRUNCHBASE_COMPANIES = gql`
    query CrunchbaseCompanies($where: cb_companies_bool_exp!, $limit: Int!, $offset: Int!) {
        companies: cb_companies(where: $where, limit: $limit, offset: $offset, order_by: { name: asc }) {
            uuid
            name
            permalink
            description: short_description
            funding: total_funding_usd
            employees: employee_count
            lastFundingType: last_funding_type
            imageId: image_id
        }
    }
`;

export const CRUNCHBASE_COMPANIES_COUNT = gql`
    query CrunchbaseCompaniesCount($where: cb_companies_bool_exp!) {
        companiesCount: cb_companies_aggregate(where: $where) {
            aggregate {
                count
            }
        }
    }
`;

export const CRUNCHBASE_CATEGORIES = gql`
    query CrunchbaseCategories {
        categories: cb_category_list(order_by: { value: asc }) {
            name: value
        }
    }
`;

export const CRUNCHBASE_FUNDING_ROUNDS = gql`
    query CrunchbaseFundingRounds {
        rounds: cb_investment_types(order_by: { investment_type: asc }) {
            round: investment_type
        }
    }
`;

export const CRUNCHBASE_INVESTORS = gql`
    query CrunchbaseInvestors($prefix: String!, $limit: Int!, $uuids: [String!]!) {
        investors: cb_investors(
            where: { _or: [{ lowercase_name: { name: { _like: $prefix } } }, { uuid: { _in: $uuids } }] }
            order_by: [{ investment_count: desc_nulls_last }]
            limit: $limit
        ) {
            name
            uuid
            investment_count
        }
    }
`;

export const CRUNCHBASE_COMPANY_STATUSES = gql`
    query CrunchbaseCompanyStatuses {
        company: cb_companies(distinct_on: status, order_by: { status: asc }) {
            status
        }
    }
`;

export const CRUNCHBASE_COUNTRIES = gql`
    query CrunchbaseCountries {
        country: cb_country_list(order_by: { country_code: asc }) {
            countryCode: country_code
        }
    }
`;

export const CRUNCHBASE_REGIONS = gql`
    query CrunchbaseRegions($where: cb_region_list_bool_exp!) {
        regions: cb_region_list(where: $where) {
            region
        }
    }
`;

export const CRUNCHBASE_CITIES = gql`
    query CrunchbaseCities($where: cb_city_org_counts_bool_exp!) {
        cities: cb_city_org_counts(where: $where, order_by: { orgs_count: desc }) {
            city
        }
    }
`;

export const CRUNCHBASE_ORGANIZATIONS = gql`
    query CBOrgsQuery($linkedinUrls: [String!]!) {
        organizations: cb_companies(where: { recruiter_url: { linkedin_recruiter_url: { _in: $linkedinUrls } } }) {
            uuid
            shortDescription: short_description
            numEmployees: employee_count
            totalFundingUSD: total_funding_usd
            permalink
            foundedYear: founded_year
            status
            revenueRange: revenue_range
            lastFundingType: last_funding_type
            lastFundingDate: last_funding_date
            valuationUSD: valuation_usd
            valuationDate: valuation_date
            city
            region
            countryCode: country_code
            company_investors(limit: 5, order_by: { investor: { investment_count: desc_nulls_last } }) {
                investorName: investor_name
                investor {
                    permalink
                }
            }
            recruiter_url {
                url: linkedin_recruiter_url
            }
        }
    }
`;
